import type { User } from "@supabase/supabase-js";

import { findAuthorizedStaffByEmail } from "@/core/repositories/auth.repository";
import { isAdminUser, isStaffUser } from "@/types/auth";
import { AuthError } from "@/lib/errors";
import { getSupabaseClient } from "@/lib/supabase";

async function getAuthenticatedUser() {
  const supabase = getSupabaseClient();
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();

  if (error || !user) {
    throw new AuthError("unauthenticated");
  }

  return user;
}

async function requireActiveStaff(): Promise<{ user: User; staffId: string }> {
  const user = await getAuthenticatedUser();

  if (!isStaffUser(user)) {
    throw new AuthError("unauthorized");
  }

  const email = user.email?.trim().toLowerCase();

  if (!email) {
    throw new AuthError("unauthorized");
  }

  const staff = await findAuthorizedStaffByEmail(email);

  if (!staff?.id) {
    throw new AuthError("unauthorized");
  }

  return { user, staffId: staff.id };
}

export async function requireStaff() {
  const { user } = await requireActiveStaff();
  return user;
}

export async function requireStaffMemberId(): Promise<string> {
  const { staffId } = await requireActiveStaff();
  return staffId;
}

export async function requireAdmin() {
  const user = await requireStaff();

  if (!isAdminUser(user)) {
    throw new AuthError("unauthorized");
  }

  return user;
}

export function canUserPublishContent(user: User): boolean {
  return isAdminUser(user);
}
